import { motion } from "framer-motion";
import { Eye, HandHeart, UserRound, Leaf } from "lucide-react";
import SEO from "@/components/SEO";
import TrustMarquee from "@/components/TrustMarquee";
import { FadeUp, Chapter, CTABand, EASE } from "@/components/Reveal";
import { Testimonials } from "@/components/Sections";
import { IMAGES } from "@/data/content";

const WERKWIJZE = [
  {
    n: "01",
    title: "Luisteren en plannen",
    text: "Elke aanvraag begint met uw situatie. Wij stemmen de planning af op uw wensen en de overdrachtsdatum.",
  },
  {
    n: "02",
    title: "Zorgvuldig opnemen",
    text: "De woning wordt nauwkeurig in kaart gebracht volgens de geldende EP-methodiek, van isolatie tot installaties.",
  },
  {
    n: "03",
    title: "Correct registreren",
    text: "Wij zorgen voor een zorgvuldige registratie in EP-online, zodat uw label officieel en aantoonbaar is.",
  },
  {
    n: "04",
    title: "Nazorg en uitleg",
    text: "U ontvangt uitleg over het resultaat en antwoord op uw vragen, ook na oplevering van het label.",
  },
];

const WAARDEN = [
  {
    icon: Eye,
    title: "Transparant",
    text: "Helder over werkwijze, planning en tarieven. U weet altijd waar u aan toe bent.",
  },
  {
    icon: HandHeart,
    title: "Betrouwbaar",
    text: "Afspraak is afspraak. Wij doen wat we beloven en houden u op de hoogte.",
  },
  {
    icon: UserRound,
    title: "Persoonlijk",
    text: "Eén vast aanspreekpunt dat met u meedenkt, van aanvraag tot registratie.",
  },
  {
    icon: Leaf,
    title: "Toekomstgericht",
    text: "Wij geloven in woningen die klaar zijn voor morgen: comfortabel en energiezuinig.",
  },
];

export default function OverOns() {
  return (
    <>
      <SEO
        title="Over Directelabels | EP-W woningopnames in Rotterdam"
        description="Maak kennis met Directelabels: professionele EP-W woningopnames voor woningeigenaren, makelaars en verhuurders in Rotterdam en omgeving, met duidelijke communicatie en transparante tarieven."
        path="/over-ons"
      />

      <section data-testid="over-ons-hero" className="mx-auto max-w-7xl px-5 pb-16 pt-14 md:px-8 md:pt-24">
        <div className="grid items-end gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE }}
              className="text-xs font-bold uppercase tracking-[0.24em] text-primary"
            >
              Over Directelabels
            </motion.p>
            <h1 data-testid="over-ons-title" className="mt-6 font-display text-4xl font-semibold leading-tight tracking-tighter text-ink md:text-6xl">
              {["Professionele EP-W opnames,", "persoonlijk geregeld."].map((line, i) => (
                <span key={line} className="block overflow-hidden pb-1">
                  <motion.span
                    className="block"
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: EASE }}
                  >
                    {i === 1 ? <span className="text-primary">{line}</span> : line}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
              className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg"
            >
              Directelabels helpt woningeigenaren, makelaars en verhuurders in Rotterdam en omgeving aan een
              energielabel voor hun woning. Wij voeren zorgvuldige EP-W woningopnames uit en begeleiden het traject van
              aanvraag tot registratie — met duidelijke communicatie en transparante tarieven.
            </motion.p>
          </div>
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.3, ease: EASE }}
              className="overflow-hidden rounded-[2rem] border border-black/5 shadow-xl shadow-energy-deep/10"
            >
              <img
                src={IMAGES.wind}
                alt="Windturbine in het Zuid-Hollandse landschap"
                className="aspect-[4/3] w-full object-cover"
                data-testid="over-ons-image"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <TrustMarquee />

      <section data-testid="werkwijze-section" className="mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-32">
        <Chapter
          number="01"
          label="Onze werkwijze"
          title="Zorgvuldig van begin tot eind"
          description="Een energielabel is een officieel document. Dat vraagt om een werkwijze die deugt, van eerste contact tot registratie."
        />
        <div className="mt-16 grid gap-10 md:grid-cols-2 lg:grid-cols-4">
          {WERKWIJZE.map((s, i) => (
            <FadeUp key={s.n} delay={i * 0.1}>
              <div data-testid={`werkwijze-step-${i}`}>
                <span className="font-display text-6xl font-light text-primary/20">{s.n}</span>
                <div className="mt-4 h-px w-full bg-gradient-to-r from-primary/30 to-transparent" aria-hidden="true" />
                <h3 className="mt-5 font-display text-lg font-medium tracking-tight text-ink md:text-xl">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.text}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      <section data-testid="waarden-section" className="border-y border-black/5 bg-white">
        <div className="mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-32">
          <Chapter
            number="02"
            label="Onze waarden"
            title="Waar wij voor staan"
            center
          />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {WAARDEN.map((w, i) => (
              <FadeUp key={w.title} delay={i * 0.1}>
                <div
                  data-testid={`waarde-card-${i}`}
                  className="group h-full rounded-3xl border border-black/5 bg-background p-8 text-center transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lg"
                >
                  <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-energy-pale text-primary transition-colors duration-300 group-hover:bg-primary group-hover:text-white">
                    <w.icon size={22} />
                  </span>
                  <h3 className="mt-6 font-display text-xl font-medium tracking-tight text-ink">{w.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{w.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <Testimonials number="03" />

      <CTABand />
    </>
  );
}
